<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from '../../components-next/icon/Icon.vue';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
  // Technical tool key (ex.: 'get_conversation') sent by the backend alongside
  // the raw content. Used to render a friendly, localized label instead of the
  // raw English "Using <tool>" string.
  functionName: {
    type: String,
    default: '',
  },
  // Optional chain-of-thought the LLM produced for this step. Rendered
  // collapsed so the attendant can inspect the reasoning on demand.
  reasoning: {
    type: String,
    default: '',
  },
});

const { t, te } = useI18n();

const showReasoning = ref(false);

// Friendly, localized label for the step. The backend persists content as
// "Using <tool>" / "Completed <tool>" (English) plus function_name. We keep the
// raw content as a fallback, but when the tool key is known we render a
// localized phrase (PT-BR for the operators) derived from the verb + a friendly
// noun for the tool.
const label = computed(() => {
  const key = props.functionName;
  const toolLabelKey = `CAPTAIN.COPILOT.THINKING.TOOLS.${key}`;
  if (!key || !te(toolLabelKey)) {
    return props.content;
  }

  const tool = t(toolLabelKey);
  const isCompleted = /^completed\b/i.test(props.content || '');
  return isCompleted
    ? t('CAPTAIN.COPILOT.THINKING.COMPLETED', { tool })
    : t('CAPTAIN.COPILOT.THINKING.USING', { tool });
});

const hasReasoning = computed(() => Boolean(props.reasoning?.trim()));
</script>

<template>
  <div
    class="flex flex-col gap-2 p-3 rounded-xl bg-n-background/50 border border-n-weak hover:bg-n-background/80 transition-colors duration-200"
  >
    <div class="flex items-start gap-2">
      <Icon
        icon="i-ri-sparkling-line"
        class="w-4 h-4 mt-0.5 flex-shrink-0 text-n-slate-9"
        aria-hidden="true"
      />
      <div class="flex flex-col gap-1 min-w-0 flex-1">
        <div class="text-sm text-n-slate-12">
          {{ label }}
        </div>
        <button
          v-if="hasReasoning"
          type="button"
          class="self-start text-xs text-n-slate-10 hover:text-n-slate-11 transition-colors duration-200"
          @click="showReasoning = !showReasoning"
        >
          {{ t('CAPTAIN.COPILOT.THINKING.REASONING_LABEL') }}
        </button>
        <div
          v-if="hasReasoning"
          v-show="showReasoning"
          class="text-xs text-n-slate-11 whitespace-pre-wrap break-words"
        >
          {{ reasoning }}
        </div>
      </div>
    </div>
  </div>
</template>
